import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useFact, STAGES, STAGE_HI } from "@/lib/factoryI18n";
import { Link } from "react-router-dom";
import { AlertTriangle, QrCode } from "lucide-react";

export default function FactoryWIP() {
  const { lang } = useLang();
  const f = useFact(lang);
  const [board, setBoard] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  useEffect(() => {
    api.get("/factory/wip-board").then((r) => { setBoard(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clear = () => setSelected(new Set());
  const printSelected = (format) => {
    if (selected.size === 0) return;
    const ids = [...selected].join(",");
    const suffix = format === "thermal" ? "&format=thermal&width=80" : "";
    window.open(`/factory/qr-labels?ids=${ids}${suffix}`, "_blank");
  };
  if (loading) return <div className="text-sm text-[#7C8E9E]">Loading…</div>;
  const cols = [...STAGES, "Rework", "Rejected"];
  return (
    <div data-testid="factory-wip">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
          {f("wip_board")}
        </h1>
        {selected.size > 0 && (
          <div className="flex items-center gap-2" data-testid="wip-bulk-toolbar">
            <span className="text-xs text-[#4A627A]">{selected.size} selected</span>
            <button onClick={() => printSelected("a4")} data-testid="wip-print-labels"
              className="px-3 py-1.5 rounded-full text-white text-xs font-semibold inline-flex items-center gap-1.5"
              style={{ background: "var(--brand-navy)" }}>
              <QrCode size={13} /> A4 labels
            </button>
            <button onClick={() => printSelected("thermal")} data-testid="wip-print-thermal"
              className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
              style={{ background: "var(--brand-gold)", color: "var(--brand-navy)" }}>
              <QrCode size={13} /> Thermal 80mm
            </button>
            <button onClick={clear} className="text-xs text-[#7C8E9E] underline">clear</button>
          </div>
        )}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {cols.map((s) => {
          const pieces = board[s] || [];
          return (
            <div key={s} className="min-w-[240px] bg-white border border-[#1B3A5C]/10 rounded-lg" data-testid={`wip-col-${s}`}>
              <div className="px-3 py-2 border-b border-[#1B3A5C]/10 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--brand-navy)" }}>
                  {lang === "hi" ? (STAGE_HI[s] || s) : s}
                </span>
                <span className="text-xs bg-[#FAF6F0] px-2 py-0.5 rounded-full">{pieces.length}</span>
              </div>
              <div className="p-2 space-y-2 max-h-[70vh] overflow-y-auto">
                {pieces.map((p) => (
                  <div key={p.id} data-testid={`wip-card-${p.id}`}
                    className={`border border-[#1B3A5C]/10 rounded-md p-2 text-xs hover:bg-[#FAF6F0] ${selected.has(p.id) ? "ring-2 ring-[#B08B49]" : ""}`}>
                    <div className="flex items-start gap-2">
                      <input type="checkbox" checked={selected.has(p.id)}
                        data-testid={`wip-select-${p.id}`}
                        onChange={() => toggle(p.id)}
                        className="mt-0.5 flex-shrink-0" />
                      <Link to={`/factory/piece/${p.id}`} className="flex-1 min-w-0">
                        <div className="font-mono font-semibold">{p.code}</div>
                        <div className="text-[#4A627A] mt-0.5">{p.design_code} · {p.size_text}</div>
                        <div className="text-[#7C8E9E] mt-0.5 text-[10px]">{p.location}</div>
                        {(p.rework_count || 0) >= 2 && (
                          <div className="mt-1 flex items-center gap-1 text-red-600 text-[10px]"><AlertTriangle size={10} /> Rework ×{p.rework_count}</div>
                        )}
                      </Link>
                    </div>
                  </div>
                ))}
                {pieces.length === 0 && <div className="text-[10px] text-[#7C8E9E] px-2">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
